import { useEffect, useState } from 'react';
import { layersApi, type LayerDto } from '../../lib/layersApi';
import { pickObjectTitle } from './useObjectCard';

/** Прототип под тендер — см. src/lib/featureFlags.ts (objectHierarchy). Two-step
 * layer-then-object picker, used both for "выберите родительский объект" (this
 * object picks its parent) and "добавить дочерний объект" (this object gains a
 * child) — reuses the same getFeatures() call the map/table already use rather
 * than a dedicated cross-layer search endpoint, so it loads a layer's full
 * feature list on selection.
 *
 * snapCheckboxEnabledForLayer, when given, shows a "переместить координаты"
 * checkbox — pass a function ignoring its argument to make it a static yes/no
 * (the "set my own parent" direction: whether *this* object is a point is
 * already known before the picker opens), or one that inspects the picked
 * layer's geometry_type (the "add a child" direction: validity depends on
 * which layer the user picks). Omit entirely to hide the checkbox — e.g. for
 * a non-point object picking its own parent, where snapping never applies.
 *
 * onCreateNew, when given, adds a "Создать новый объект" mode alongside
 * picking an existing one — a brand-new child has no established position of
 * its own yet, so there's nothing to lose by always placing it at this
 * object's coordinates; the caller is expected to create the feature and
 * snap it (see ObjectCardBody's handleAddChild), this component just tells it
 * which layer to create in. */
export function ParentObjectPicker({
  mapId,
  excludeFeatureId,
  snapCheckboxLabel,
  snapCheckboxEnabledForLayer,
  onCreateNew,
  onPick,
  onCancel,
}: {
  mapId: string;
  excludeFeatureId: string;
  snapCheckboxLabel?: string;
  snapCheckboxEnabledForLayer?: (layer: LayerDto | undefined) => boolean;
  onCreateNew?: (layerId: string) => void;
  onPick: (featureId: string, layerId: string, snapToParent: boolean) => void;
  onCancel: () => void;
}) {
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [layers, setLayers] = useState<LayerDto[]>([]);
  const [layerId, setLayerId] = useState('');
  const [features, setFeatures] = useState<{ id: string; title: string }[]>([]);
  const [featureId, setFeatureId] = useState('');
  const [loading, setLoading] = useState(false);
  const [snap, setSnap] = useState(false);

  useEffect(() => {
    layersApi.listLayers(mapId).then(setLayers).catch(() => setLayers([]));
  }, [mapId]);

  useEffect(() => {
    setFeatureId('');
    if (!layerId || mode !== 'existing') {
      setFeatures([]);
      return;
    }
    setLoading(true);
    layersApi
      .getFeatures(layerId)
      .then((fc) =>
        setFeatures(
          fc.features
            .filter((f) => f.id !== excludeFeatureId)
            .map((f) => ({ id: f.id, title: pickObjectTitle(f.properties) })),
        ),
      )
      .catch(() => setFeatures([]))
      .finally(() => setLoading(false));
  }, [layerId, excludeFeatureId, mode]);

  const selectedLayer = layers.find((l) => l.id === layerId);
  const snapEnabled = snapCheckboxEnabledForLayer?.(selectedLayer) ?? false;
  // A freshly created object has no position worth keeping — always snap it.
  const effectiveSnap = mode === 'new' ? true : snap && snapEnabled;
  const canCreateNew = Boolean(onCreateNew) && snapEnabled;

  return (
    <div className="mt-2 flex flex-col gap-2 rounded-lg border border-border-default bg-bg-surface-secondary p-3">
      <select
        value={layerId}
        onChange={(e) => setLayerId(e.target.value)}
        className="h-9 rounded-md border border-border-default bg-bg-surface px-2 text-[12px] text-text-primary outline-none"
      >
        <option value="">— выберите слой —</option>
        {layers.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
      </select>

      {onCreateNew && (
        <div className="flex gap-1 rounded-md bg-bg-surface p-0.5">
          <button
            type="button"
            onClick={() => setMode('existing')}
            className={`h-7 flex-1 rounded text-[11px] font-medium ${mode === 'existing' ? 'bg-action-primary text-text-inverse' : 'text-text-secondary'}`}
          >
            Выбрать существующий
          </button>
          <button
            type="button"
            onClick={() => canCreateNew && setMode('new')}
            disabled={!canCreateNew}
            title={!canCreateNew && layerId ? 'Недоступно для этого типа геометрии' : undefined}
            className={`h-7 flex-1 rounded text-[11px] font-medium disabled:cursor-not-allowed disabled:opacity-40 ${
              mode === 'new' ? 'bg-action-primary text-text-inverse' : 'text-text-secondary'
            }`}
          >
            Создать новый
          </button>
        </div>
      )}

      {mode === 'existing' ? (
        <select
          value={featureId}
          onChange={(e) => setFeatureId(e.target.value)}
          disabled={!layerId || loading}
          className="h-9 rounded-md border border-border-default bg-bg-surface px-2 text-[12px] text-text-primary outline-none disabled:opacity-50"
        >
          <option value="">{loading ? 'Загрузка…' : '— выберите объект —'}</option>
          {features.map((f) => (
            <option key={f.id} value={f.id}>
              {f.title}
            </option>
          ))}
        </select>
      ) : (
        <p className="text-[11px] text-text-secondary">Новый объект будет создан в выбранном слое.</p>
      )}

      {snapCheckboxLabel && (
        <label
          className={`flex items-center gap-2 text-[12px] ${snapEnabled ? 'text-text-primary' : 'text-text-secondary opacity-50'}`}
        >
          <input
            type="checkbox"
            checked={effectiveSnap}
            disabled={!snapEnabled || mode === 'new'}
            onChange={(e) => setSnap(e.target.checked)}
            className="size-[15px] accent-action-primary"
          />
          {snapCheckboxLabel}
          {layerId && !snapEnabled && ' (недоступно для этого типа геометрии)'}
        </label>
      )}
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="text-[12px] font-medium text-text-secondary hover:text-text-primary">
          Отмена
        </button>
        <button
          type="button"
          disabled={mode === 'existing' ? !featureId : !layerId}
          onClick={() => (mode === 'new' ? onCreateNew?.(layerId) : onPick(featureId, layerId, effectiveSnap))}
          className="rounded-md bg-action-primary px-3 py-1.5 text-[12px] font-medium text-text-inverse disabled:cursor-not-allowed disabled:opacity-50"
        >
          {mode === 'new' ? 'Создать' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
}
